import {
  AfterViewInit,
  ApplicationRef,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  NgZone,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { Order, OrderComponent } from "models/order-management/models";
import { OrderFormState } from "pages/orders-entry-om/add-order/order-form-state";
import { DictationTranscriptionService } from "providers/dictation-transcription/dictation-transcription.service";
import { StateManagementService } from "providers/state-management/state-management.service";

type BinaryAttribute = "on" | "off";
type CapitalizationManner =
  | "characters"
  | "none"
  | "sentences"
  | "words"
  | BinaryAttribute;
type SpellCheck = true | false | "default";
type TextWrap = "hard" | "soft" | "off";

// Current version of Angular does not support the MediaRecorder from the
// Browser API: https://developer.mozilla.org/en-US/docs/Web/API/MediaRecorder
type MediaRecorder = any;
type MediaStream = any;

@Component({
  selector: "voice-ion-textarea",
  templateUrl: "voice-ion-textarea.component.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => VoiceIonTextArea),
      multi: true,
    },
  ],
})
export class VoiceIonTextArea implements AfterViewInit, ControlValueAccessor {
  /****************************************************************************
   * Inputs                                                                   *
   ****************************************************************************/
  @Input("autocapitalize") autoCapitalize: CapitalizationManner = "off";
  @Input("autocomplete") autoComplete: BinaryAttribute = "off";
  @Input("autocorrect") autoCorrect: BinaryAttribute = "off";
  @Input("clearInput") clearInput: boolean;
  @Input("clearOnEdit") clearOnEdit: boolean = false;
  @Input("cols") cols: string = "20";
  @Input("data-cy") dataCy: string;
  @Input("disabled") disabled: boolean = false;
  @Input("form") form: string;
  @Input("max") max: any;
  @Input("maxlength") maxLength: string;
  @Input("min") min: any;
  @Input("minlength") minLength: string;
  @Input("name") name: string;
  @Input("placeholder") placeholder: string;
  @Input("readonly") readonly: boolean = false;
  @Input("required") required: boolean = false;
  @Input("rows") rows: string = "2";
  @Input("spellcheck") spellCheck: SpellCheck = "default";
  @Input("step") step: any;
  @Input("type") type:
    | "text"
    | "password"
    | "email"
    | "number"
    | "search"
    | "tel"
    | "url" = "text";
  @Input("wrap") wrap: TextWrap = "soft";
  @Input() orderComponent: OrderComponent;
  @Input() orderCreateRequest: Order;
  // Required to match style of dynamic inputs when used in a dynamic form
  @Input() isDynamicInput: boolean = false;
  // Required to remove grid padding when not required
  @Input() removeGridPadding: boolean = false;
  @Input() get orderFormState(): OrderFormState {
    return this._orderFormState;
  }
  @Input() forceGlobalChangeDetection: boolean = false;

  //
  private _orderFormState: OrderFormState;
  isDictationTranscriptionServiceAvailable: boolean = false;
  _textAreaValue: string = "";

  // Control Value Accessor Instance Variables / Methods
  value: any;
  onChange = (value: any) => {};
  onTouched = () => {};

  // Audio Canvas-Related Instance Variables
  audioCtx: any;
  mediaRecorder: MediaRecorder;
  mediaStream: MediaStream;

  // Button-Related Instance Variables
  shouldStartRecording: boolean = false;
  shouldDisplayAudioIcon: boolean = false;
  shouldDisplayTimer: boolean = false;

  // Error-Related Instance Variables
  recordingError: string = null;

  // Loading state for transcription
  isTranscribing: boolean = false;

  // Track component destruction to avoid calling detectChanges on destroyed component
  private isDestroyed = false;

  // Timer-Related Instance Variables
  MM: string = null;
  SS: string = null;
  TIME_LIMIT: number = 240;
  timePassed: number = 0;
  timeRemaining: number = this.TIME_LIMIT;
  timerInterval: any;
  T_MINUS_240_TO_229_SECONDS: Array<number>;
  T_MINUS_228_TO_217_SECONDS: Array<number>;
  T_MINUS_216_TO_205_SECONDS: Array<number>;
  T_MINUS_204_TO_193_SECONDS: Array<number>;
  T_MINUS_192_TO_181_SECONDS: Array<number>;
  T_MINUS_180_TO_169_SECONDS: Array<number>;
  T_MINUS_168_TO_157_SECONDS: Array<number>;
  T_MINUS_156_TO_145_SECONDS: Array<number>;
  T_MINUS_144_TO_133_SECONDS: Array<number>;
  T_MINUS_132_TO_121_SECONDS: Array<number>;
  T_MINUS_120_TO_109_SECONDS: Array<number>;
  T_MINUS_108_TO_097_SECONDS: Array<number>;
  T_MINUS_096_TO_085_SECONDS: Array<number>;
  T_MINUS_084_TO_073_SECONDS: Array<number>;
  T_MINUS_072_TO_061_SECONDS: Array<number>;
  T_MINUS_060_TO_049_SECONDS: Array<number>;
  T_MINUS_048_TO_037_SECONDS: Array<number>;
  T_MINUS_036_TO_025_SECONDS: Array<number>;
  T_MINUS_024_TO_013_SECONDS: Array<number>;
  T_MINUS_012_TO_001_SECONDS: Array<number>;

  constructor(
    public stateMgmtSrvc: StateManagementService,
    private changeDetectorRef: ChangeDetectorRef,
    private dictationTranscriptionSrvc: DictationTranscriptionService,
    private ngZone: NgZone,
    private appRef: ApplicationRef
  ) {
    this.formatTimeRemaining(this.timeRemaining);
    this.T_MINUS_240_TO_229_SECONDS = this.range(240, 228, 1);
    this.T_MINUS_228_TO_217_SECONDS = this.range(228, 216, 1);
    this.T_MINUS_216_TO_205_SECONDS = this.range(216, 204, 1);
    this.T_MINUS_204_TO_193_SECONDS = this.range(204, 192, 1);
    this.T_MINUS_192_TO_181_SECONDS = this.range(192, 180, 1);
    this.T_MINUS_180_TO_169_SECONDS = this.range(180, 168, 1);
    this.T_MINUS_168_TO_157_SECONDS = this.range(168, 156, 1);
    this.T_MINUS_156_TO_145_SECONDS = this.range(156, 144, 1);
    this.T_MINUS_144_TO_133_SECONDS = this.range(144, 132, 1);
    this.T_MINUS_132_TO_121_SECONDS = this.range(132, 120, 1);
    this.T_MINUS_120_TO_109_SECONDS = this.range(120, 108, 1);
    this.T_MINUS_108_TO_097_SECONDS = this.range(108, 96, 1);
    this.T_MINUS_096_TO_085_SECONDS = this.range(96, 84, 1);
    this.T_MINUS_084_TO_073_SECONDS = this.range(84, 72, 1);
    this.T_MINUS_072_TO_061_SECONDS = this.range(72, 60, 1);
    this.T_MINUS_060_TO_049_SECONDS = this.range(60, 48, 1);
    this.T_MINUS_048_TO_037_SECONDS = this.range(48, 36, 1);
    this.T_MINUS_036_TO_025_SECONDS = this.range(36, 24, 1);
    this.T_MINUS_024_TO_013_SECONDS = this.range(24, 12, 1);
    this.T_MINUS_012_TO_001_SECONDS = this.range(12, 0, 1);
  }

  /****************************************************************************
   * COMPONENT LIFECYCLE METHODS                                              *
   ****************************************************************************/
  ngAfterViewInit(): void {
    this.setIsDictationTranscriptionServiceAvailable();
  }

  async init() {
    // Main block for doing the audio recording
    if (navigator.mediaDevices.getUserMedia) {
      console.log("The mediaDevices.getUserMedia() method is supported.");

      const constraints = { audio: true };
      let chunks = [];

      let onSuccess = (stream) => {
        this.mediaRecorder = new MediaRecorder(stream);

        this.mediaRecorder.onstop = (e) => {
          console.log("Last data to read (after MediaRecorder.stop() called).");
          chunks = [];
          console.log("recorder stopped");
        };

        this.mediaRecorder.ondataavailable = (e) => {
          chunks.push(e.data);

          this.ngZone.run(() => {
            this.isTranscribing = true;

            if (!this.isDestroyed) {
              this.changeDetectorRef.detectChanges();
            }
          });

          // Only proceed with transcription if component is not destroyed
          if (this.isDestroyed) {
            return;
          }

          this.dictationTranscriptionSrvc
            .postDictationTranscription(e.data)
            .subscribe({
              next: (response) => {
                this.ngZone.run(() => {
                  this.clearRecordingError();
                  const prior = (this._textAreaValue || "").trim();
                  const next = (response.transcribe || "").trim();
                  const merged =
                    prior && next ? `${prior} ${next}` : prior || next;
                  this.onTextAreaValueChange(merged);
                  this.isTranscribing = false;

                  if (!this.isDestroyed) {
                    this.changeDetectorRef.detectChanges();
                  }
                });
              },
              error: () => {
                this.ngZone.run(() => {
                  this.setRecordingError(
                    "An error occurred while transcribing. Please try again"
                  );
                  this.isTranscribing = false;

                  if (!this.isDestroyed) {
                    this.changeDetectorRef.detectChanges();
                  }
                });
              },
            });
        };
      };

      let onError = function (err) {
        console.log("The following error occured: " + err);
      };

      const mediaStreamPromise =
        navigator.mediaDevices.getUserMedia(constraints);

      mediaStreamPromise.then(onSuccess).catch(onError);
      this.mediaStream = await mediaStreamPromise;
    } else {
      console.log("MediaDevices.getUserMedia() not supported on your browser!");
    }
  }

  ngOnDestroy() {
    this.isDestroyed = true;

    // Stop recording if it's active
    if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
      this.mediaRecorder.stop();
    }

    // Clear timer interval to prevent ViewDestroyedError
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }

    // Reset UI states to prevent any further updates
    this.isTranscribing = false;
    this.shouldStartRecording = false;
    this.shouldDisplayAudioIcon = false;
    this.shouldDisplayTimer = false;

    if (this.mediaStream) {
      this.mediaStream
        .getTracks()
        .filter((track) => track.readyState === "live")
        .forEach((track) => {
          track.stop();
        });
    }
  }

  /****************************************************************************
   * Cypress-Related ("data-cy") Methods                                      *
   ****************************************************************************/
  createDataCyAttribute(component: string, option: string): string {
    return `${this.toKebabCase(component)}-option-${this.toKebabCase(option)}`;
  }

  toKebabCase(label: string): string {
    if (label) {
      return label
        .replace(/^\s+|\s+$/gm, "")
        .replace(/([a-z])([A-Z])/g, "$1-$2")
        .replace(/[\s_]+/g, "-")
        .toLowerCase();
    }
    return "";
  }

  /****************************************************************************
   * CONTROL VALUE ACCESSOR IMPLEMENTATION                                    *
   ****************************************************************************/
  get textAreaValue(): string {
    return this._textAreaValue;
  }

  set textAreaValue(value: string) {
    this._textAreaValue = value;
    this.propagateChange(this._textAreaValue);
  }

  propagateChange = (_: any) => {};

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(value: any): void {
    if (value !== undefined) {
      this._textAreaValue = value;
    }
  }

  /****************************************************************************
   * Helper Functions                                                         *
   ****************************************************************************/
  onTextAreaValueChange = (event: string) => {
    this.textAreaValue = event;
    this.clearRecordingError();
  };

  /****************************************************************************
   * Error Handling Functions                                                 *
   ****************************************************************************/
  setRecordingError(errorMessage: string): void {
    this.recordingError = errorMessage;
  }

  clearRecordingError(): void {
    this.recordingError = null;
  }

  range(start: number, stop: number, step: number): Array<number> {
    return Array.from(
      { length: Math.ceil((start - stop) / step) },
      (_, i) => start - i * step
    );
  }

  setIsDictationTranscriptionServiceAvailable() {
    this.stateMgmtSrvc.showAiVoiceDictationTranscriptionComponents.subscribe(
      (value) => {
        this.isDictationTranscriptionServiceAvailable = value;
        if (this.isDictationTranscriptionServiceAvailable) {
          this.init();
        }
      }
    );
  }

  /****************************************************************************
   * ICON: Audio Recording Indicator                                          *
   ****************************************************************************/

  toggleAudioIconVisibility(): void {
    this.shouldDisplayAudioIcon = !this.shouldDisplayAudioIcon;
  }

  /****************************************************************************
   * BUTTON: Start / Stop Audio Recording Functions                           *
   ****************************************************************************/

  buttonIsSetToStartRecording(): boolean {
    return !this.shouldStartRecording;
  }

  onButtonClick(): void {
    // Don't allow clicks when component is destroyed
    if (this.isDestroyed) {
      return;
    }

    // Don't allow clicks when transcribing
    if (this.isTranscribing) {
      return;
    }

    // Clear any previous errors when starting new recording
    this.clearRecordingError();

    // Clear transcribing state when starting new recording
    this.isTranscribing = false;

    // Set Audio Icon Visibility
    this.toggleAudioIconVisibility();

    // Turn Timer On / Off & Set Visibility
    // NOTE: Must Happen Before Toggling Button Icon & Label
    this.toggleTimerOnAndOff();
    this.toggleTimerVisibility();

    // Toggle recording state
    this.shouldStartRecording = !this.shouldStartRecording;

    if (this.shouldStartRecording) {
      this.startRecording();
    } else {
      this.stopRecording();
    }
  }

  startRecording() {
    this.mediaRecorder.start();
    console.log(
      `Recorder started. Recorder state is now ${this.mediaRecorder.state}`
    );
  }

  stopRecording() {
    this.mediaRecorder.stop();
    console.log(
      `Recorder stopped. Recorder state is now ${this.mediaRecorder.state}`
    );
  }

  /****************************************************************************
   * TIMER: Recording Time Remaining Functions                                *
   ****************************************************************************/
  updateRecordingTimeRemainingHTMLElement(timeRemaining: number): void {
    switch (true) {
      case this.T_MINUS_240_TO_229_SECONDS.includes(timeRemaining):
        this.amendRecordingTimeRemainingHTMLElementColor(
          "t-minus-240-seconds",
          "t-minus-240-to-229-seconds"
        );
        break;
      case this.T_MINUS_228_TO_217_SECONDS.includes(timeRemaining):
        this.amendRecordingTimeRemainingHTMLElementColor(
          "t-minus-240-to-229-seconds",
          "t-minus-228-to-217-seconds"
        );
        break;
      case this.T_MINUS_216_TO_205_SECONDS.includes(timeRemaining):
        this.amendRecordingTimeRemainingHTMLElementColor(
          "t-minus-228-to-217-seconds",
          "t-minus-216-to-205-seconds"
        );
        break;
      case this.T_MINUS_204_TO_193_SECONDS.includes(timeRemaining):
        this.amendRecordingTimeRemainingHTMLElementColor(
          "t-minus-216-to-205-seconds",
          "t-minus-204-to-193-seconds"
        );
        break;
      case this.T_MINUS_192_TO_181_SECONDS.includes(timeRemaining):
        this.amendRecordingTimeRemainingHTMLElementColor(
          "t-minus-204-to-193-seconds",
          "t-minus-192-to-181-seconds"
        );
        break;
      case this.T_MINUS_180_TO_169_SECONDS.includes(timeRemaining):
        this.amendRecordingTimeRemainingHTMLElementColor(
          "t-minus-192-to-181-seconds",
          "t-minus-180-to-169-seconds"
        );
        break;
      case this.T_MINUS_168_TO_157_SECONDS.includes(timeRemaining):
        this.amendRecordingTimeRemainingHTMLElementColor(
          "t-minus-180-to-169-seconds",
          "t-minus-168-to-157-seconds"
        );
        break;
      case this.T_MINUS_156_TO_145_SECONDS.includes(timeRemaining):
        this.amendRecordingTimeRemainingHTMLElementColor(
          "t-minus-168-to-157-seconds",
          "t-minus-156-to-145-seconds"
        );
        break;
      case this.T_MINUS_144_TO_133_SECONDS.includes(timeRemaining):
        this.amendRecordingTimeRemainingHTMLElementColor(
          "t-minus-156-to-145-seconds",
          "t-minus-144-to-133-seconds"
        );
        break;
      case this.T_MINUS_132_TO_121_SECONDS.includes(timeRemaining):
        this.amendRecordingTimeRemainingHTMLElementColor(
          "t-minus-144-to-133-seconds",
          "t-minus-132-to-121-seconds"
        );
        break;
      case this.T_MINUS_120_TO_109_SECONDS.includes(timeRemaining):
        this.amendRecordingTimeRemainingHTMLElementColor(
          "t-minus-132-to-121-seconds",
          "t-minus-120-to-109-seconds"
        );
        break;
      case this.T_MINUS_108_TO_097_SECONDS.includes(timeRemaining):
        this.amendRecordingTimeRemainingHTMLElementColor(
          "t-minus-120-to-109-seconds",
          "t-minus-108-to-097-seconds"
        );
        break;
      case this.T_MINUS_096_TO_085_SECONDS.includes(timeRemaining):
        this.amendRecordingTimeRemainingHTMLElementColor(
          "t-minus-108-to-097-seconds",
          "t-minus-096-to-085-seconds"
        );
        break;
      case this.T_MINUS_084_TO_073_SECONDS.includes(timeRemaining):
        this.amendRecordingTimeRemainingHTMLElementColor(
          "t-minus-096-to-085-seconds",
          "t-minus-084-to-073-seconds"
        );
        break;
      case this.T_MINUS_072_TO_061_SECONDS.includes(timeRemaining):
        this.amendRecordingTimeRemainingHTMLElementColor(
          "t-minus-084-to-073-seconds",
          "t-minus-072-to-061-seconds"
        );
        break;
      case this.T_MINUS_060_TO_049_SECONDS.includes(timeRemaining):
        this.amendRecordingTimeRemainingHTMLElementColor(
          "t-minus-072-to-061-seconds",
          "t-minus-060-to-049-seconds"
        );
        break;
      case this.T_MINUS_048_TO_037_SECONDS.includes(timeRemaining):
        this.amendRecordingTimeRemainingHTMLElementColor(
          "t-minus-060-to-049-seconds",
          "t-minus-048-to-037-seconds"
        );
        break;
      case this.T_MINUS_036_TO_025_SECONDS.includes(timeRemaining):
        this.amendRecordingTimeRemainingHTMLElementColor(
          "t-minus-048-to-037-seconds",
          "t-minus-036-to-025-seconds"
        );
        break;
      case this.T_MINUS_024_TO_013_SECONDS.includes(timeRemaining):
        this.amendRecordingTimeRemainingHTMLElementColor(
          "t-minus-036-to-025-seconds",
          "t-minus-024-to-013-seconds"
        );
        break;
      case this.T_MINUS_012_TO_001_SECONDS.includes(timeRemaining):
        this.amendRecordingTimeRemainingHTMLElementColor(
          "t-minus-024-to-013-seconds",
          "t-minus-012-to-001-seconds"
        );
        break;
      default:
        this.amendRecordingTimeRemainingHTMLElementColor(
          "t-minus-012-to-001-seconds",
          "t-minus-000-seconds"
        );
    }
  }

  amendRecordingTimeRemainingHTMLElementColor(
    oldClass: string,
    newClass: string
  ): void {
    const htmlElement: HTMLElement = document.getElementById(
      "recording-time-remaining"
    );
    if (htmlElement && !htmlElement.classList.contains(newClass)) {
      htmlElement.classList.replace(oldClass, newClass);
    }
  }

  formatTimeRemaining(time: number): void {
    // The largest round integer less than or equal to the result of time divided being by 60.
    let minutes: number = Math.floor(time / 60);

    // Seconds are the remainder of the time divided by 60 (modulus operator)
    let seconds: number = time % 60;

    // If the value of seconds is less than 10, then display seconds with a leading zero
    this.MM = minutes < 10 ? `0${minutes}` : `${minutes}`;

    // If the value of seconds is less than 10, then display seconds with a leading zero
    this.SS = seconds < 10 ? `0${seconds}` : `${seconds}`;
  }

  initializeTimerVariables(): void {
    this.timePassed = 0;
    this.timeRemaining = this.TIME_LIMIT;
  }

  resetTimer(): void {
    clearInterval(this.timerInterval);
    this.initializeTimerVariables();
    this.formatTimeRemaining(this.timeRemaining);
  }

  startTimer(): void {
    this.timerInterval = setInterval(() => {
      // Check if component is destroyed before proceeding
      if (this.isDestroyed) {
        clearInterval(this.timerInterval);
        return;
      }

      // The amount of time passed increments by one
      this.timePassed = this.timePassed += 1;
      this.timeRemaining = this.TIME_LIMIT - this.timePassed;
      this.formatTimeRemaining(this.timeRemaining);
      this.updateRecordingTimeRemainingHTMLElement(this.timeRemaining);
      if (this.timeRemaining === -1) {
        this.onButtonClick();
      }
      if (!this.isDestroyed) {
        this.ngZone.run(() => {
          try {
            this.changeDetectorRef.detectChanges();
            // Only force global change detection if the flag is set
            if (this.forceGlobalChangeDetection) {
              this.appRef.tick();
            }
          } catch (error) {
            // If detectChanges fails, clear the interval to prevent further errors
            console.warn(
              "ViewDestroyedError caught in timer, clearing interval"
            );
            clearInterval(this.timerInterval);
          }
        });
      }
    }, 1000);
  }

  toggleTimerOnAndOff(): void {
    if (this.buttonIsSetToStartRecording()) {
      this.startTimer();
    } else {
      this.resetTimer();
    }
  }

  toggleTimerVisibility(): void {
    this.shouldDisplayTimer = !this.shouldDisplayTimer;
  }
}



// WEBPACK FOOTER //
// ./src/components/common/voice-ion-textarea/voice-ion-textarea.component.ts